import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Search, Plus, Menu, X, PanelLeftClose, PanelLeft, Bell, LogOut, ExternalLink,
} from 'lucide-react';
import { Button } from '../../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import { useAuth, api } from '../../../context/AuthContext';
import { ADMIN_NAV, QUICK_CREATE } from './adminNav';
import GlobalSearch from './GlobalSearch';
import { toast } from 'sonner';

const NavItems = ({ collapsed, onNavigate, testPrefix = 'admin-nav' }) => {
  const navigate = useNavigate();
  return (
    <nav className="flex-1 overflow-y-auto py-3">
      {ADMIN_NAV.map((group) => (
        <div key={group.section} className="px-3 mb-4">
          {!collapsed && (
            <p className="px-2 mb-1.5 text-[10px] uppercase tracking-[0.14em] text-white/35">{group.section}</p>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              if (!item.available) {
                return (
                  <button
                    key={item.label}
                    onClick={() => toast.info(`${item.label} is coming soon`)}
                    className="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-sm text-white/30 cursor-not-allowed"
                    title={`${item.label} — coming soon`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </button>
                );
              }
              return (
                <NavLink
                  key={item.label}
                  to={item.path}
                  end={item.path === '/admin'}
                  onClick={() => { onNavigate?.(); navigate(item.path); }}
                  className={({ isActive }) => cn(
                    'w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors',
                    isActive ? 'bg-primary text-white font-medium' : 'text-white/70 hover:bg-white/[0.07] hover:text-white'
                  )}
                  data-testid={`${testPrefix}-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
};

/**
 * Responsive administration shell: persistent sidebar on desktop, drawer on
 * mobile, with global search and quick-create in the top bar.
 */
export const AdminShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [attentionCount, setAttentionCount] = useState(null);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    api.get('/admin/workspace/attention?limit=1')
      .then((res) => setAttentionCount(res.data.total ?? 0))
      .catch(() => setAttentionCount(null));
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-shell">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden lg:flex fixed inset-y-0 left-0 z-30 flex-col bg-[#0B1220] transition-[width] duration-200',
          collapsed ? 'w-[68px]' : 'w-[232px]'
        )}
        data-testid="admin-sidebar"
      >
        <div className={cn('flex items-center h-14 px-4', collapsed && 'justify-center px-0')}>
          <button onClick={() => navigate('/admin')} className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-primary flex-shrink-0" />
            {!collapsed && <span className="text-white font-heading text-sm">Flippex Admin</span>}
          </button>
        </div>
        <NavItems collapsed={collapsed} />
        <div className="p-3 border-t border-white/[0.06]">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-sm text-white/50 hover:text-white hover:bg-white/[0.07]"
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /> Collapse</>}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="admin-mobile-nav">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[270px] bg-[#0B1220] flex flex-col">
            <div className="flex items-center justify-between h-14 px-4">
              <span className="text-white font-heading text-sm">Flippex Admin</span>
              <button onClick={() => setMobileOpen(false)} className="text-white/60" data-testid="admin-mobile-nav-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <NavItems onNavigate={() => setMobileOpen(false)} testPrefix="admin-mobile-nav" />
          </div>
        </div>
      )}

      {/* Content column */}
      <div className={cn('transition-[padding] duration-200', collapsed ? 'lg:pl-[68px]' : 'lg:pl-[232px]')}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-black/[0.06]">
          <div className="flex items-center gap-3 h-14 px-4 md:px-6">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-1.5 -ml-1.5 rounded-md hover:bg-slate-100"
              data-testid="admin-mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>

            <button
              onClick={() => setSearchOpen(true)}
              className="flex-1 max-w-md flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-100/80 hover:bg-slate-100 text-sm text-foreground-muted transition-colors"
              data-testid="admin-search-btn"
            >
              <Search className="h-4 w-4" />
              <span className="truncate">Search Flippex administration…</span>
              <kbd className="hidden md:inline ml-auto text-[10px] text-foreground-muted/70">⌘K</kbd>
            </button>

            <div className="ml-auto flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="rounded-lg" data-testid="quick-create-btn">
                    <Plus className="h-4 w-4 md:mr-1.5" />
                    <span className="hidden md:inline">Create</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="text-xs">Quick create</DropdownMenuLabel>
                  {QUICK_CREATE.map((action) => (
                    <DropdownMenuItem key={action.label} onClick={() => navigate(action.path)}>
                      {action.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                onClick={() => navigate('/admin/tasks')}
                className="relative p-2 rounded-lg hover:bg-slate-100"
                data-testid="admin-attention-btn"
                title="Tasks & approvals"
              >
                <Bell className="h-4 w-4" />
                {attentionCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] font-medium flex items-center justify-center">
                    {attentionCount}
                  </span>
                )}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-8 h-8 rounded-full bg-slate-900 text-white text-xs font-medium flex items-center justify-center"
                    data-testid="admin-profile-btn"
                  >
                    {(user?.full_name || user?.email || 'A').slice(0, 1).toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs font-normal text-foreground-muted truncate">
                    {user?.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/')}>
                    <ExternalLink className="h-4 w-4 mr-2" /> View investor site
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/tools')}>
                    Module launcher
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 py-6 md:py-8">{children}</main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
};

export default AdminShell;
